import React from 'react';
import { Grid, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../components/brazilian-number-input';
import { ValidationErrors } from '../../../../utils/validation';
import { LookupSelect, toLookupOptions } from '../../shared/lookup-select';
import { PurchaseItemField, REAL_ESTATE_CATEGORY } from '../context/purchase-validation';
import { IItemPurchaseAddEditRequest } from '../../../../model/scp/purchase/request/purchase-item-add-edit-request';
import { ITableResponse } from '../../../../model/scp/purchase/response/tables-response';

const DESCRIPTION_MAX_LENGTH = 2048;

export interface PurchaseItemFormProps {
    itemPurchase: IItemPurchaseAddEditRequest;
    materialService: ITableResponse[];
    benefitType: ITableResponse[];
    judgementCriteria: ITableResponse[];
    categoryItemPurchase: ITableResponse[];
    purchaseItemSituation: ITableResponse[];
    // Situação só existe para itens já publicados
    showSituation: boolean;
    errors: ValidationErrors<PurchaseItemField>;
    onChange: (e: any) => void;
    onAmountChange: (field: 'quantity' | 'unitValueEstimated', value: number) => void;
}

export default function PurchaseItemForm({
    itemPurchase, materialService, benefitType, judgementCriteria, categoryItemPurchase, purchaseItemSituation, showSituation, errors, onChange, onAmountChange,
}: PurchaseItemFormProps) {
    const descriptionLength = itemPurchase.description?.length ?? 0;
    const isRealEstate = String(itemPurchase.categoryItemId) === REAL_ESTATE_CATEGORY;

    return (
        <React.Fragment>
            {showSituation && (
                <FormDrawerGroup title="Situação">
                    <LookupSelect label="Situação" name="select-situation-purchase-item" value={itemPurchase.situationPurchaseItemId} options={toLookupOptions(purchaseItemSituation)} onChange={onChange} emptyLabel="Selecionar" />
                </FormDrawerGroup>
            )}

            <FormDrawerGroup title="Classificação do item">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required label="Tipo" name="select-type-item" value={itemPurchase.typeItemId} options={toLookupOptions(materialService)} onChange={onChange} error={errors.typeItemId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required label="Categoria" name="select-category-item" value={itemPurchase.categoryItemId} options={categoryItemPurchase.map((c) => ({ value: c.id, label: `${c.id} - ${c.name}` }))} onChange={onChange} error={errors.categoryItemId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required label="Tipo de Benefício" name="select-type-benefit" value={itemPurchase.typeBenefitId} options={benefitType.map((b) => ({ value: b.id, label: `${b.id} - ${b.name}` }))} onChange={onChange} error={errors.typeBenefitId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LookupSelect required label="Critério de Julgamento" name="select-criterion-judgment" value={itemPurchase.criterionJudgmentId} options={judgementCriteria.map((j) => ({ value: j.id, label: `${j.id} - ${j.name}` }))} onChange={onChange} error={errors.criterionJudgmentId} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Valores e medidas" description="O valor total é calculado pela quantidade e pelo valor unitário.">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Unidade de Medida</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="input-unit-measure" inputProps={{ maxLength: 30 }} value={itemPurchase.unitMeasure || ''} onChange={onChange} error={!!errors.unitMeasure} />
                        <FieldError message={errors.unitMeasure} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Quantidade</InputLabel>
                        <BrazilianNumberInput fullWidth value={itemPurchase.quantity} onChange={(value) => onAmountChange('quantity', value)} decimals={4} placeholder="0,0000" error={!!errors.quantity} />
                        <FieldError message={errors.quantity} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Valor Unitário Estimado</InputLabel>
                        <BrazilianNumberInput fullWidth value={itemPurchase.unitValueEstimated} onChange={(value) => onAmountChange('unitValueEstimated', value)} decimals={4} prefix="R$" placeholder="0,0000" error={!!errors.unitValueEstimated} />
                        <FieldError message={errors.unitValueEstimated} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Valor Total</InputLabel>
                        <BrazilianNumberInput fullWidth value={itemPurchase.amount} disabled decimals={4} prefix="R$" placeholder="0,0000" />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Descrição">
                <InputLabel required>Descrição</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" multiline rows={6} name="input-description" inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }} value={itemPurchase.description || ''} onChange={onChange} error={!!errors.description} />
                <FieldError message={errors.description} />
                <Typography variant="caption" color={descriptionLength >= DESCRIPTION_MAX_LENGTH ? 'error' : 'text.secondary'} sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                    {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
                </Typography>
            </FormDrawerGroup>

            <FormDrawerGroup title="Informações complementares">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Patrimônio</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="input-patrimony" inputProps={{ maxLength: 255 }} value={itemPurchase.patrimony || ''} onChange={onChange} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required={isRealEstate}>Código Registro Imobiliário</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" name="input-real-estate-registry-code" inputProps={{ maxLength: 255 }} value={itemPurchase.realEstateRegistryCode || ''} onChange={onChange} error={!!errors.realEstateRegistryCode} />
                        <FieldError message={errors.realEstateRegistryCode} />
                    </Grid>
                    <Grid item xs={12}>
                        <SwitchField label="Incentivo Produtivo Básico" checked={itemPurchase.basicProductiveIncentive || false} onChange={(checked) => onChange({ target: { name: 'check-basic-produtive-incentive', checked } })} />
                    </Grid>
                    <Grid item xs={12}>
                        <SwitchField label="Orçamento Sigiloso" checked={itemPurchase.budgetConfidential || false} onChange={(checked) => onChange({ target: { name: 'check-budget-confidential', checked } })} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </React.Fragment>
    );
}
